import { Receipt, X } from 'lucide-react';
import { ProgressRing } from './ProgressRing';
import { StepRow } from './StepRow';
import { COPY, ONBOARDING_STEPS } from '../config/onboarding.config';
import type {
  ActiveCompany,
  CompanyProgress,
  StepKey,
} from '../config/onboarding.types';

interface ChecklistPanelProps {
  id: string;
  company: ActiveCompany;
  progress: CompanyProgress;
  completed: number;
  remaining: number;
  isReady: boolean;
  openStepKey: StepKey | null;
  onClose: () => void;
  onHide: () => void;
  onToggleStep: (key: StepKey) => void;
  onGoToStep: (key: StepKey) => void;
  onIssue: () => void;
  className?: string;
}

/**
 * Popover que se abre sobre el lanzador. Sin franja de logros: los checks de
 * cada paso ya cumplen esa función. Sin RUC visible: el nombre identifica a
 * la empresa y el RUC queda en el tooltip.
 */
export function ChecklistPanel({
  id,
  company,
  progress,
  completed,
  remaining,
  isReady,
  openStepKey,
  onClose,
  onHide,
  onToggleStep,
  onGoToStep,
  onIssue,
  className = '',
}: ChecklistPanelProps) {
  const compact = !company.isFirstCompany;
  const title = COPY.title(company.isFirstCompany);

  // Si falta un solo paso, la meta lo nombra; si no, el texto general.
  const pending = ONBOARDING_STEPS.filter((s) => !progress[s.key]);
  const lockedText = pending.length === 1 ? pending[0].unlockHint : COPY.goalLocked;

  return (
    <section
      id={id}
      role="dialog"
      aria-label={title}
      className={[
        'w-[min(340px,calc(100vw-24px))] max-h-[calc(100dvh-140px)] origin-bottom-right overflow-y-auto',
        'rounded-[14px] border border-muted bg-surface-0',
        'shadow-[0_18px_40px_-12px_rgba(15,23,42,0.30),0_2px_6px_rgba(15,23,42,0.06)]',
        'motion-safe:animate-popUp',
        className,
      ].join(' ')}
    >
      {/* Cabecera: anillo, título fijo y contador. */}
      <div className="flex items-center gap-[11px] py-3.5 pl-3.5 pr-2.5">
        <ProgressRing value={completed} size={36} />
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-ui-base font-semibold leading-snug text-primary">{title}</h2>
          <p className={['mt-0.5 text-caption', isReady ? 'font-semibold text-brand' : 'text-tertiary'].join(' ')}>
            {isReady ? COPY.ready : COPY.remaining(remaining)}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={COPY.close}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-1"
        >
          <X className="h-4 w-4" strokeWidth={2.2} />
        </button>
      </div>

      {/* Empresa activa: dato crítico en multiempresa. */}
      <div className="flex items-center gap-2 border-y border-muted bg-surface-1 px-4 py-[7px]">
        <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] bg-brand font-display text-[8.5px] font-bold text-inverse">
          {initials(company.name)}
        </span>
        <span
          className="min-w-0 truncate text-caption font-medium text-tertiary"
          title={`${company.name} (RUC ${company.ruc})`}
        >
          {company.name}
        </span>
      </div>

      <div className="px-[9px] pb-0.5 pt-[5px]">
        {ONBOARDING_STEPS.map((step, i) => (
          <StepRow
            key={step.key}
            step={step}
            isDone={progress[step.key]}
            isOpen={!compact && !progress[step.key] && openStepKey === step.key}
            isLast={i === ONBOARDING_STEPS.length - 1}
            compact={compact}
            onToggle={() => onToggleStep(step.key)}
            onGo={() => onGoToStep(step.key)}
          />
        ))}
      </div>

      {/* Meta. Pendiente es un panel sobrio; al habilitarse se tiñe de marca. */}
      <div
        className={[
          'mx-3.5 mb-3 mt-1.5 rounded-[10px] border p-[13px_14px] transition-all duration-500 ease-[cubic-bezier(.32,.72,0,1)]',
          isReady
            ? 'border-brand bg-gradient-to-b from-[var(--primary-light)] to-[var(--surface-0)]'
            : 'border-muted bg-surface-1',
        ].join(' ')}
      >
        <div className="flex items-center gap-2.5">
          <span
            className={[
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all duration-500',
              isReady ? 'border-brand bg-brand text-inverse' : 'border-muted bg-surface-0 text-subtle',
            ].join(' ')}
          >
            <Receipt className="h-[15px] w-[15px]" strokeWidth={1.9} />
          </span>
          <span>
            <span
              className={[
                'block font-display text-ui-base font-semibold leading-snug transition-colors',
                isReady ? 'text-primary' : 'text-tertiary',
              ].join(' ')}
            >
              {COPY.goalTitle}
            </span>
            <span
              className={[
                'mt-0.5 block text-caption transition-colors',
                isReady ? 'font-medium text-brand' : 'text-subtle',
              ].join(' ')}
            >
              {isReady ? COPY.goalReady : lockedText}
            </span>
          </span>
        </div>

        {isReady && (
          <button
            type="button"
            onClick={onIssue}
            className="mt-3 block w-full rounded-[6px] bg-brand p-2.5 text-body-sm font-medium text-inverse transition-colors hover:bg-brand-hover active:bg-brand-pressed"
          >
            {COPY.goalAction}
          </button>
        )}
      </div>

      <div className="flex justify-end px-[15px] pb-3">
        <button
          type="button"
          onClick={onHide}
          className="p-[3px_2px] text-caption text-subtle transition-colors hover:text-tertiary hover:underline"
        >
          {COPY.hide}
        </button>
      </div>
    </section>
  );
}

/** Iniciales ignorando conectores y sufijos societarios. */
function initials(name: string): string {
  const stop = ['de', 'del', 'la', 'el', 'y', 'sac', 'sa', 'eirl', 'srl', 's', 'a', 'c', 'e', 'i', 'r', 'l'];
  const words = name
    .replace(/[.,]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !stop.includes(w.toLowerCase()));
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase();
}
